import { sortAsc } from '@strata/helper-functions';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import { TherapyGroupsApiUrl } from 'services/TherapyGroupHelpers';

const ALREADY_IN_GROUP = {
    secondaryData: 'Already in a Therapy Group',
    disabled: true,
};

const takenParentNames = (therapyGroups, linkedParentNames) => {
    const fromGroups = (therapyGroups ?? []).flatMap(
        (group) => group.active_parent_product_services ?? [],
    );
    return new Set([...fromGroups, ...(linkedParentNames ?? [])]);
};

const parentProductOption = (pps, taken) => ({
    name: pps.display_name,
    parent_product_service: pps.parent_product_service,
    ...(taken.has(pps.parent_product_service) ? ALREADY_IN_GROUP : {}),
});

const parentProductOptions = (parentProducts, taken) =>
    (parentProducts ?? [])
        .map((pps) => parentProductOption(pps, taken))
        .sort(({ name: a }, { name: b }) => sortAsc(a, b));

export const AddProductDialog = ({
    isOpen,
    parentProducts = undefined,
    linkedParentNames,
    onAdd,
    onClose,
}) => {
    const therapyGroups = useGet(isOpen ? TherapyGroupsApiUrl : null);
    const options = parentProductOptions(
        parentProducts,
        takenParentNames(therapyGroups, linkedParentNames),
    );

    return (
        <Formik
            initialValues={{ parentProductService: '' }}
            validationSchema={Yup.object({
                parentProductService: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await onAdd(values.parentProductService);
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to add product.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Add Product"
                    primaryButtonLabel="Add Product"
                    secondaryButtonLabel="Cancel"
                    width={600}
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        formik.isSubmitting || !(formik.dirty && formik.isValid)
                    }
                    dialogContent={
                        <SuggestSingleSelectFormik
                            id="parentProductService"
                            label="Product"
                            required
                            values={options}
                            itemsEqual="parent_product_service"
                            onChange={(item) =>
                                formik.setFieldValue(
                                    'parentProductService',
                                    item?.parent_product_service ?? '',
                                )
                            }
                            selectedItem={options.find(
                                (option) =>
                                    option.parent_product_service ===
                                    formik.values.parentProductService,
                            )}
                        />
                    }
                />
            )}
        </Formik>
    );
};

AddProductDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    parentProducts: PropTypes.arrayOf(PropTypes.shape({})),
    linkedParentNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAdd: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
